import { useTenantedClient, useWhoami } from '../stores/org';
import { useSuspenseQuery } from '@tanstack/react-query';
import { queryKeys } from './query-cache-keys';
import { Assert } from '@cotera/utilities';
import {
  ToolListItem as ToolListItemResponse,
  type ToolContextKind,
} from '../etc/api/typed-client';
import { SLACK_PROVIDER_KEY } from '@cotera/client/v0/components/tool-connections/slack-provider';
import { providerForToolKey } from '@cotera/api';

export type AvailableTool = ToolListItemResponse & {
  extractedProvider: string;
  displayPath: string[];
};

export type ProviderConfigStatus = 'configured' | 'unconfigured';

export type ProviderGroup = {
  provider: string;
  displayName: string;
  description?: string;
  tools: AvailableTool[];
  configStatus: ProviderConfigStatus;
  configuredToolCount: number;
  totalToolCount: number;
};

/**
 * Extract the provider from a tool key.
 * e.g., "google-sheets/sheets/appendRow" -> "google-sheets"
 *
 * Shared with the server's provider filter so a tool always groups under the
 * provider whose page can actually list it.
 */
function extractProvider(toolKey: string): string {
  return providerForToolKey(toolKey);
}

/**
 * Extract the display path from a tool key (excluding the provider).
 * e.g., "google-sheets/sheets/appendRow" -> ["sheets", "appendRow"]
 */
function extractDisplayPath(toolKey: string): string[] {
  const segments = toolKey.split('/');
  return segments.slice(1);
}

// Strategies that require no user authentication
const NO_AUTH_STRATEGIES = ['platform_managed', 'none'];

/**
 * Check if a provider requires no authentication.
 * Returns true if all tools only have platform_managed or none strategy.
 */
function isNoAuthProvider(tools: AvailableTool[]): boolean {
  return tools.every((tool) => {
    const nonLegacyStrategies = tool.credentialStrategyDescriptors.filter(
      (s) => s.name !== 'tool_by_name'
    );
    return (
      nonLegacyStrategies.length === 1 &&
      NO_AUTH_STRATEGIES.includes(nonLegacyStrategies[0]?.name ?? '')
    );
  });
}

/** Legacy tool keys kept for agents; omit from tool connections catalog. */
const isHiddenFromToolConnections = (tool: ToolListItemResponse): boolean =>
  tool.hidden === true;

/**
 * Whether a tool can run in `context`.
 *
 * A missing `availableContexts` means the server predates the field, so treat
 * it as unconstrained rather than hiding everything.
 */
export const toolRunsInContext = (
  tool: Pick<ToolListItemResponse, 'availableContexts'>,
  context: ToolContextKind | undefined
): boolean =>
  context === undefined ||
  tool.availableContexts === undefined ||
  tool.availableContexts.includes(context);

export type UseAvailableToolsOptions = {
  /**
   * Only include tools that can run here. Omit for the full catalog (tool
   * connections), pass 'workflow' for the workflow builder's picker.
   */
  context?: ToolContextKind;
};

/**
 * Hook to fetch available tools from the tools-v2 endpoint.
 * Returns tools grouped by provider for display in the tool connections UI.
 */
export const useAvailableTools = (
  options: UseAvailableToolsOptions = {}
): {
  tools: AvailableTool[];
  providers: ProviderGroup[];
} => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const { data: rawTools } = useSuspenseQuery({
    queryKey: queryKeys.toolsV2.all({ orgId }),
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.list());
    },
  });

  const providerMap = new Map<string, ToolListItemResponse[]>();
  for (const tool of rawTools) {
    // Filter here as well as in buildProviderGroup: a provider whose every tool
    // is excluded must not survive the `tools.length > 0` check below and drill
    // into an empty page.
    if (
      isHiddenFromToolConnections(tool) ||
      !toolRunsInContext(tool, options.context)
    ) {
      continue;
    }
    const provider = extractProvider(tool.key);
    const existing = providerMap.get(provider);
    if (existing) {
      existing.push(tool);
    } else {
      providerMap.set(provider, [tool]);
    }
  }

  const providers: ProviderGroup[] = Array.from(providerMap.entries())
    .map(([providerKey, providerRawTools]) =>
      buildProviderGroup(providerRawTools, providerKey, options.context)
    )
    .filter((p) => p.tools.length > 0)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return { tools: providers.flatMap((p) => p.tools), providers };
};

/**
 * Build ProviderGroup from raw tools for a single provider.
 */
function buildProviderGroup(
  rawTools: ToolListItemResponse[],
  providerKey: string,
  context?: ToolContextKind
): ProviderGroup {
  const tools: AvailableTool[] = rawTools
    .filter(
      (tool) =>
        !isHiddenFromToolConnections(tool) &&
        toolRunsInContext(tool, context) &&
        tool.implementationType !== 'legacy' &&
        tool.implementationType !== 'agent'
    )
    .map((tool) => ({
      ...tool,
      extractedProvider: extractProvider(tool.key),
      displayPath: extractDisplayPath(tool.key),
    }));

  const slackPrimaryTool =
    providerKey === SLACK_PROVIDER_KEY
      ? tools.find((t) => extractProvider(t.key) === SLACK_PROVIDER_KEY)
      : undefined;
  const displayName =
    slackPrimaryTool?.providerDisplayName ??
    tools[0]?.providerDisplayName ??
    providerKey;
  const description =
    slackPrimaryTool?.providerDescription ?? tools[0]?.providerDescription;
  const sortedTools = [...tools].sort((a, b) => a.name.localeCompare(b.name));
  const configuredToolCount = sortedTools.filter((t) => t.canExecute).length;
  const requiresNoAuth = isNoAuthProvider(sortedTools);
  const configStatus: ProviderConfigStatus =
    configuredToolCount > 0 || requiresNoAuth ? 'configured' : 'unconfigured';

  return {
    provider: providerKey,
    displayName,
    description,
    tools: sortedTools,
    configStatus,
    configuredToolCount,
    totalToolCount: sortedTools.length,
  };
}

/**
 * Org-scoped tools for one provider (TanStack Query cache key
 * {@link queryKeys.toolsV2.forProvider}). This is the shared source for tool
 * connections UI and credential OAuth sections — same cache as
 * {@link useProviderTools}.
 */
export const useToolsV2ProviderToolsList = (
  providerKey: string
): ToolListItemResponse[] => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const { data } = useSuspenseQuery({
    queryKey: queryKeys.toolsV2.forProvider({
      orgId,
      provider: providerKey,
    }),
    queryFn: async () => {
      return Assert.assertOk(
        await client.toolsV2.listToolsForProvider({
          provider: providerKey,
        })
      );
    },
  });

  return data;
};

/**
 * Fetch tools for a single provider via listToolsForProvider. Fast - only loads that provider.
 */
export const useProviderTools = (providerKey: string): ProviderGroup | null => {
  const rawTools = useToolsV2ProviderToolsList(providerKey);

  if (rawTools.length === 0) {
    return null;
  }

  return buildProviderGroup(rawTools, providerKey);
};
